import React, { useState, useEffect, useCallback } from "react";
import {
  Box, CircularProgress, Typography, Chip, IconButton,
  Tooltip, TextField, Collapse, useTheme, Switch,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import FindInPageOutlinedIcon from "@mui/icons-material/FindInPageOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import GavelIcon from "@mui/icons-material/Gavel";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import BusinessIcon from "@mui/icons-material/Business";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  fetchKeyInfo, extractKeyInfo, updateKeyInfoField,
  fetchLawDependencies, fetchRegulatoryAlerts, updateAlertStatus,
} from "../../../../redux/actions/contractAction";

// ── Inline Editable Field ───────────────────────────────────────────────────

const EditableField = ({ label, value, fieldName, contractId, onSave, theme }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || "");
  const isDark = theme.palette.mode === "dark";

  useEffect(() => { setEditValue(value || ""); }, [value]);

  const handleSave = () => {
    onSave(fieldName, editValue);
    setEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value || "");
    setEditing(false);
  };

  const displayValue = typeof value === "boolean"
    ? (value ? "Yes" : "No")
    : (value || "—");

  if (editing) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
        <Typography sx={{ fontSize: "0.72rem", color: theme.palette.text.secondary, minWidth: 90, flexShrink: 0 }}>
          {label}
        </Typography>
        <TextField
          size="small"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          autoFocus
          sx={{
            flex: 1,
            "& .MuiInputBase-root": { fontSize: "0.78rem", height: 28, borderRadius: "6px" },
            "& .MuiInputBase-input": { py: 0.25, px: 1 },
          }}
        />
        <IconButton size="small" onClick={handleSave} sx={{ width: 22, height: 22, color: theme.palette.success.main }}>
          <CheckIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <IconButton size="small" onClick={handleCancel} sx={{ width: 22, height: 22, color: theme.palette.text.secondary }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex", alignItems: "center", gap: 0.5, mb: 0.75,
        cursor: "pointer", borderRadius: "6px", py: 0.25, px: 0.5, mx: -0.5,
        "&:hover": { backgroundColor: alpha(theme.palette.action.hover, 0.04) },
        "&:hover .edit-icon": { opacity: 1 },
      }}
      onClick={() => setEditing(true)}
    >
      <Typography sx={{ fontSize: "0.72rem", color: theme.palette.text.secondary, minWidth: 90, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: "0.78rem", color: value ? theme.palette.text.primary : theme.palette.text.disabled,
        flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        {displayValue}
      </Typography>
      <EditOutlinedIcon className="edit-icon" sx={{ fontSize: 12, color: theme.palette.text.disabled, opacity: 0, transition: "opacity 0.15s" }} />
    </Box>
  );
};

// ── Collapsible Section ─────────────────────────────────────────────────────

const Section = ({ title, icon, children, defaultOpen = true, theme, count }) => {
  const [open, setOpen] = useState(defaultOpen);
  const isDark = theme.palette.mode === "dark";

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex", alignItems: "center", gap: 0.75, cursor: "pointer",
          py: 0.5, px: 0.5, mx: -0.5, borderRadius: "6px",
          "&:hover": { backgroundColor: alpha(theme.palette.action.hover, 0.06) },
        }}
      >
        {icon}
        <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, flex: 1 }}>{title}</Typography>
        {count !== undefined && (
          <Chip
            label={count}
            size="small"
            sx={{
              height: 18, fontSize: "0.65rem", fontWeight: 600,
              backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.15 : 0.1),
              color: theme.palette.primary.main,
            }}
          />
        )}
        {open ? <ExpandLessIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ pl: 0.5, pt: 0.5 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

// ── Party Card ──────────────────────────────────────────────────────────────

const PartyCard = ({ party, theme }) => {
  const isDark = theme.palette.mode === "dark";
  const isCompany = party.party_type === "company";

  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1, p: 1, mb: 0.75,
      borderRadius: "8px",
      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
      backgroundColor: isDark ? alpha("#fff", 0.02) : "#fff",
    }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: "8px", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.15 : 0.08),
        color: theme.palette.primary.main,
      }}>
        {isCompany ? <BusinessIcon sx={{ fontSize: 15 }} /> : <PersonOutlineIcon sx={{ fontSize: 15 }} />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, lineHeight: 1.2 }}>
          {party.party_name}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5, mt: 0.25, flexWrap: "wrap" }}>
          <Chip
            label={party.party_role}
            size="small"
            sx={{
              height: 16, fontSize: "0.62rem", fontWeight: 500,
              backgroundColor: alpha(theme.palette.info.main, 0.1),
              color: theme.palette.info.main,
            }}
          />
          <Chip
            label={party.party_type}
            size="small"
            sx={{
              height: 16, fontSize: "0.62rem", fontWeight: 500,
              backgroundColor: alpha(theme.palette.text.secondary, 0.08),
              color: theme.palette.text.secondary,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

// ── Regulatory Alerts Card ────────────────────────────────────────────────

const impactColors = {
  action_required: "error",
  review_recommended: "warning",
  informational: "info",
};

const RegulatoryAlertsCard = ({ alerts, theme, isDark, t, onMarkReviewed }) => {
  const [expanded, setExpanded] = useState(false);
  const maxImpact = alerts.some(a => a.impact_level === "action_required")
    ? "error"
    : alerts.some(a => a.impact_level === "review_recommended")
      ? "warning"
      : "info";
  const borderColor = theme.palette[maxImpact].main;

  return (
    <Box sx={{
      mb: 1.5, borderRadius: "10px",
      border: `1px solid ${alpha(borderColor, 0.5)}`,
      backgroundColor: alpha(borderColor, isDark ? 0.08 : 0.04),
      overflow: "hidden",
    }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: "flex", alignItems: "center", gap: 0.75, p: 1, cursor: "pointer",
          "&:hover": { backgroundColor: alpha(borderColor, 0.06) },
        }}
      >
        <WarningAmberOutlinedIcon sx={{ fontSize: 18, color: borderColor }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: borderColor }}>
            {t("keyInfo.regulatoryChanges.title", "Regulatory Changes")}
          </Typography>
          <Typography sx={{ fontSize: "0.68rem", color: theme.palette.text.secondary }}>
            {alerts.length} {t("keyInfo.regulatoryChanges.subtitle", "changes affect this contract")}
          </Typography>
        </Box>
        {expanded
          ? <ExpandLessIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
          : <ExpandMoreIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
        }
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 1, pb: 1 }}>
          {alerts.map((alert, i) => {
            const color = impactColors[alert.impact_level] || "info";
            return (
              <Box key={alert.id || i} sx={{
                p: 0.75, mb: 0.5, borderRadius: "6px",
                border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                backgroundColor: isDark ? alpha("#fff", 0.02) : "#fff",
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}>
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, flex: 1 }}>
                    {alert.doc_number}
                    {alert.article_ref && (
                      <Typography component="span" sx={{ fontSize: "0.68rem", color: theme.palette.text.secondary, ml: 0.5 }}>
                        {t("keyInfo.lawRef.article", "Article")} {alert.article_ref}
                      </Typography>
                    )}
                  </Typography>
                  <Chip
                    label={t(`keyInfo.regulatoryChanges.${alert.impact_level}`, alert.impact_level)}
                    size="small"
                    sx={{
                      height: 16, fontSize: "0.58rem", fontWeight: 600,
                      backgroundColor: alpha(theme.palette[color].main, 0.12),
                      color: theme.palette[color].main,
                    }}
                  />
                </Box>
                {alert.change_summary && (
                  <Typography sx={{ fontSize: "0.68rem", color: theme.palette.text.secondary, mb: 0.5 }}>
                    {alert.change_summary}
                  </Typography>
                )}
                <Box
                  component="button"
                  onClick={(e) => { e.stopPropagation(); onMarkReviewed(alert.id); }}
                  sx={{
                    all: "unset", cursor: "pointer", fontSize: "0.65rem", fontWeight: 600,
                    color: theme.palette.primary.main,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {t("keyInfo.regulatoryChanges.markReviewed", "Mark as Reviewed")}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

// ── Law Reference Card ────────────────────────────────────────────────────

const LawRefCard = ({ dep, theme, isDark, t }) => {
  const [showSnippet, setShowSnippet] = useState(false);

  const docTypeColors = {
    loi: theme.palette.primary.main,
    dahir: theme.palette.warning.main,
    decret: theme.palette.info.main,
    arrete: theme.palette.success.main,
  };
  const chipColor = docTypeColors[dep.doc_type?.toLowerCase()] || theme.palette.text.secondary;

  return (
    <Box sx={{
      p: 0.75, mb: 0.5, borderRadius: "8px",
      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
      backgroundColor: isDark ? alpha("#fff", 0.02) : "#fff",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Chip
          label={dep.doc_type || "ref"}
          size="small"
          sx={{
            height: 18, fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase",
            backgroundColor: alpha(chipColor, 0.12),
            color: chipColor,
          }}
        />
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, flex: 1 }}>
          {dep.doc_number}
        </Typography>
        {dep.article_ref && (
          <Typography sx={{ fontSize: "0.68rem", color: theme.palette.text.secondary }}>
            {t("keyInfo.lawRef.article", "Art.")} {dep.article_ref}
          </Typography>
        )}
        {dep.resolved ? (
          <Tooltip title={t("keyInfo.lawRef.resolved", "Resolved")}>
            <CheckCircleOutlineIcon sx={{ fontSize: 14, color: theme.palette.success.main }} />
          </Tooltip>
        ) : (
          <Tooltip title={t("keyInfo.lawRef.unresolved", "Not found in database")}>
            <ErrorOutlineIcon sx={{ fontSize: 14, color: theme.palette.warning.main }} />
          </Tooltip>
        )}
      </Box>
      {dep.resolved && dep.legislation_title && (
        <Typography sx={{ fontSize: "0.65rem", color: theme.palette.text.secondary, mt: 0.25, lineHeight: 1.3 }}>
          {dep.legislation_title}
        </Typography>
      )}
      {dep.context_snippet && (
        <>
          <Typography
            onClick={() => setShowSnippet(!showSnippet)}
            sx={{
              fontSize: "0.65rem", color: theme.palette.text.disabled, mt: 0.25, cursor: "pointer",
              overflow: "hidden", textOverflow: "ellipsis",
              whiteSpace: showSnippet ? "normal" : "nowrap",
              "&:hover": { color: theme.palette.text.secondary },
            }}
          >
            {dep.context_snippet}
          </Typography>
        </>
      )}
    </Box>
  );
};

// ── Main Component ──────────────────────────────────────────────────────────

const KeyInfo = ({ contractId, startKeyInfoExtraction }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const keyInfo = useSelector((state) => state.contract.keyInfo);
  const loadingKeyInfo = useSelector((state) => state.contract.loadingKeyInfo);
  const extractingKeyInfo = useSelector((state) => state.contract.extractingKeyInfo);
  const lawDependencies = useSelector((state) => state.contract.lawDependencies);
  const regulatoryAlerts = useSelector((state) => state.contract.regulatoryAlerts);
  const isRTL = i18n.language === "ar";

  useEffect(() => {
    if (contractId) {
      dispatch(fetchKeyInfo(contractId));
      dispatch(fetchLawDependencies(contractId));
      dispatch(fetchRegulatoryAlerts(contractId));
    }
  }, [contractId, dispatch]);

  const handleExtract = async () => {
    if (!contractId) return;
    await startKeyInfoExtraction();
  };

  const handleFieldSave = useCallback((fieldName, value) => {
    dispatch(updateKeyInfoField(contractId, { [fieldName]: value }));
  }, [contractId, dispatch]);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString(i18n.language || "fr", { year: "numeric", month: "long", day: "numeric" });
    } catch {
      return dateStr;
    }
  };

  const hasData = keyInfo && keyInfo.extraction_status === "completed";
  const isLoading = loadingKeyInfo || extractingKeyInfo;

  return (
    <Box sx={{
      width: "100%", height: "100%", overflowY: "auto", display: "flex",
      flexDirection: "column", direction: isRTL ? "rtl" : "ltr",
    }}>
      {/* ── Header ── */}
      <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`, flexShrink: 0 }}>
        <Chip
          label={t("keyInfo.chip", "AI-Powered")}
          size="small"
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.12),
            color: theme.palette.primary.main,
            fontWeight: 600, fontSize: "0.68rem", height: 20, mb: 0.75,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          }}
        />
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", letterSpacing: "-0.01em", lineHeight: 1.2 }}>
              {t("keyInfo.title", "Informations clés")}
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: theme.palette.text.secondary, mt: 0.25 }}>
              {t("keyInfo.subtitle", "Détails extraits du contrat")}
            </Typography>
          </Box>
          <Tooltip title={hasData ? t("keyInfo.reExtract", "Re-extraire") : t("keyInfo.extract", "Extraire les informations")}>
            <span>
              <IconButton
                onClick={handleExtract}
                disabled={isLoading}
                size="small"
                sx={{
                  mt: 0.25, width: 30, height: 30,
                  border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                  borderRadius: "8px",
                  color: theme.palette.text.secondary,
                  transition: "all 0.15s",
                  "&:hover": { color: theme.palette.text.primary, borderColor: alpha(theme.palette.text.secondary, 0.5) },
                  "&.Mui-disabled": { opacity: 0.35 },
                }}
              >
                {isLoading ? <CircularProgress size={13} color="inherit" /> : hasData ? <RefreshIcon sx={{ fontSize: 15 }} /> : <PlayArrowRoundedIcon sx={{ fontSize: 17 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Regulatory Alerts Card ── */}
      {regulatoryAlerts && regulatoryAlerts.filter(a => a.status === "unreviewed").length > 0 && (
        <RegulatoryAlertsCard
          alerts={regulatoryAlerts.filter(a => a.status === "unreviewed")}
          theme={theme}
          isDark={isDark}
          t={t}
          onMarkReviewed={(alertId) => dispatch(updateAlertStatus(alertId, "reviewed"))}
        />
      )}

      {/* ── Content ── */}
      {hasData ? (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {/* Contract Type Badge */}
          {keyInfo.contract_type_detected && (
            <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 0.75 }}>
              <DescriptionOutlinedIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
              <Chip
                label={keyInfo.contract_type_detected}
                size="small"
                sx={{
                  height: 22, fontSize: "0.72rem", fontWeight: 600,
                  backgroundColor: alpha(theme.palette.warning.main, isDark ? 0.15 : 0.1),
                  color: theme.palette.warning.main,
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                }}
              />
            </Box>
          )}

          {/* Parties Section */}
          <Section
            title={t("keyInfo.sections.parties", "Parties")}
            icon={<PeopleOutlineIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />}
            theme={theme}
            count={keyInfo.parties?.length}
          >
            {keyInfo.parties && keyInfo.parties.length > 0 ? (
              keyInfo.parties.map((party, i) => (
                <PartyCard key={i} party={party} theme={theme} />
              ))
            ) : (
              <Typography sx={{ fontSize: "0.75rem", color: theme.palette.text.disabled, py: 0.5 }}>
                {t("keyInfo.notFound", "Non trouvé dans le contrat")}
              </Typography>
            )}
          </Section>

          {/* Key Dates Section */}
          <Section
            title={t("keyInfo.sections.dates", "Dates clés")}
            icon={<CalendarTodayIcon sx={{ fontSize: 14, color: theme.palette.info.main }} />}
            theme={theme}
          >
            <EditableField label={t("keyInfo.fields.effective_date", "Date d'effet")} value={formatDate(keyInfo.effective_date)} fieldName="effective_date" contractId={contractId} onSave={handleFieldSave} theme={theme} />
            <EditableField label={t("keyInfo.fields.expiration_date", "Expiration")} value={formatDate(keyInfo.expiration_date)} fieldName="expiration_date" contractId={contractId} onSave={handleFieldSave} theme={theme} />
            <EditableField label={t("keyInfo.fields.renewal_date", "Renouvellement")} value={formatDate(keyInfo.renewal_date)} fieldName="renewal_date" contractId={contractId} onSave={handleFieldSave} theme={theme} />
            <EditableField label={t("keyInfo.fields.signature_date", "Signature")} value={formatDate(keyInfo.signature_date)} fieldName="signature_date" contractId={contractId} onSave={handleFieldSave} theme={theme} />
            <EditableField label={t("keyInfo.fields.notice_period", "Préavis")} value={keyInfo.notice_period} fieldName="notice_period" contractId={contractId} onSave={handleFieldSave} theme={theme} />
            <EditableField label={t("keyInfo.fields.duration", "Durée")} value={keyInfo.duration} fieldName="duration" contractId={contractId} onSave={handleFieldSave} theme={theme} />
            {keyInfo.additional_dates && keyInfo.additional_dates.length > 0 && (
              keyInfo.additional_dates.map((d, i) => (
                <EditableField key={i} label={d.label} value={d.date} fieldName={`additional_dates`} contractId={contractId} onSave={handleFieldSave} theme={theme} />
              ))
            )}
          </Section>

          {/* Financial Section */}
          <Section
            title={t("keyInfo.sections.financial", "Termes financiers")}
            icon={<AttachMoneyIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />}
            theme={theme}
          >
            <EditableField label={t("keyInfo.fields.total_value", "Valeur totale")} value={keyInfo.total_value} fieldName="total_value" contractId={contractId} onSave={handleFieldSave} theme={theme} />
            <EditableField label={t("keyInfo.fields.currency", "Devise")} value={keyInfo.currency} fieldName="currency" contractId={contractId} onSave={handleFieldSave} theme={theme} />
            <EditableField label={t("keyInfo.fields.payment_terms", "Conditions")} value={keyInfo.payment_terms} fieldName="payment_terms" contractId={contractId} onSave={handleFieldSave} theme={theme} />
            <EditableField label={t("keyInfo.fields.penalties", "Pénalités")} value={keyInfo.penalties} fieldName="penalties" contractId={contractId} onSave={handleFieldSave} theme={theme} />
            <EditableField label={t("keyInfo.fields.price_per_unit", "Prix unitaire")} value={keyInfo.price_per_unit} fieldName="price_per_unit" contractId={contractId} onSave={handleFieldSave} theme={theme} />
          </Section>

          {/* Legal & Metadata Section */}
          <Section
            title={t("keyInfo.sections.legal", "Juridique & Métadonnées")}
            icon={<GavelIcon sx={{ fontSize: 14, color: theme.palette.error.main }} />}
            theme={theme}
          >
            <EditableField label={t("keyInfo.fields.governing_law", "Droit applicable")} value={keyInfo.governing_law} fieldName="governing_law" contractId={contractId} onSave={handleFieldSave} theme={theme} />
            <EditableField label={t("keyInfo.fields.jurisdiction", "Juridiction")} value={keyInfo.jurisdiction} fieldName="jurisdiction" contractId={contractId} onSave={handleFieldSave} theme={theme} />
            <EditableField label={t("keyInfo.fields.dispute_resolution", "Résolution")} value={keyInfo.dispute_resolution} fieldName="dispute_resolution" contractId={contractId} onSave={handleFieldSave} theme={theme} />
            <EditableField label={t("keyInfo.fields.confidentiality_period", "Confidentialité")} value={keyInfo.confidentiality_period} fieldName="confidentiality_period" contractId={contractId} onSave={handleFieldSave} theme={theme} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75, py: 0.25, px: 0.5, mx: -0.5 }}>
              <Typography sx={{ fontSize: "0.72rem", color: theme.palette.text.secondary, minWidth: 90, flexShrink: 0 }}>
                {t("keyInfo.fields.auto_renewal", "Renouvellement auto")}
              </Typography>
              <Switch
                size="small"
                checked={keyInfo.auto_renewal || false}
                onChange={(e) => handleFieldSave("auto_renewal", e.target.checked)}
                sx={{ transform: "scale(0.75)" }}
              />
            </Box>
            <EditableField label={t("keyInfo.fields.termination", "Résiliation")} value={keyInfo.termination_conditions} fieldName="termination_conditions" contractId={contractId} onSave={handleFieldSave} theme={theme} />
          </Section>

          {/* Law References Section */}
          {lawDependencies && lawDependencies.length > 0 && (
            <Section
              title={t("keyInfo.sections.lawReferences", "Références juridiques")}
              icon={<MenuBookOutlinedIcon sx={{ fontSize: 15, color: theme.palette.secondary.main }} />}
              theme={theme}
              count={lawDependencies.length}
              defaultOpen={false}
            >
              {lawDependencies.map((dep, i) => (
                <LawRefCard key={dep.id || i} dep={dep} theme={theme} isDark={isDark} t={t} />
              ))}
            </Section>
          )}
        </Box>
      ) : !isLoading && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 6, gap: 1.5 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: "12px",
            backgroundColor: alpha(theme.palette.text.secondary, 0.08),
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <FindInPageOutlinedIcon sx={{ fontSize: 24, color: theme.palette.text.disabled }} />
          </Box>
          <Typography sx={{ fontSize: "0.82rem", color: theme.palette.text.secondary, textAlign: "center" }}>
            {t("keyInfo.emptyState", "Cliquez sur Extraire pour analyser votre contrat")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default KeyInfo;
